/**
 * Agents API (DEV-232 / DEV-364).
 * agents-service — через СГЕНЕРИРОВАННЫЙ клиент `agentsServiceApi` (`@/api`, общий
 * axios с auth+locale interceptors): эндпоинты и тела запросов синхронны с proto.
 * Возвращаемые типы оставлены «ручными» (agent.types) как удобный интерфейс фичи —
 * сгенерированные V1*-модели делают все поля optional, поэтому маппим `.data` через `as`.
 *
 * auth (публичные ручки, DEV-229): сброс пароля и подтверждение email — общие для всех
 * ролей, остаются на прямом apiClient (это auth-шлюз, не agents-service).
 */
import { agentsServiceApi, apiClient } from '@/api'
import type {
  AgentBotLinkCode,
  AgentEducationVideo,
  AgentVideoProgress,
  GetEducationVideosResponse,
  PlaybackEvent,
  AgentClientComment,
  AgentDashboard,
  AgentDealComment,
  AgentDealTask,
  AgentFreeSlot,
  AgentProfile,
  AgentProjectMaterial,
  CreateDealTaskRequest,
  MaterialCategory,
  AgentRegisterRequest,
  AgentRegisterResponse,
  AgentTicket,
  AgentTicketMessage,
  AgentTicketThread,
  AskCommissionResponse,
  CheckLeadUniqueRequest,
  CheckLeadUniqueResponse,
  CompanyCandidate,
  CompanyVerification,
  CompanyVerificationStatus,
  ConfirmPasswordResetRequest,
  GetMyClientsResponse,
  GetMyDealsResponse,
  GetMyTicketsResponse,
  GetWebinarsResponse,
  InquiryType,
  RequestMeetingPayload,
  RequestMeetingResponse,
  RequestPasswordResetRequest,
  SubmitInquiryResponse,
  SubmitLeadRequest,
  SubmitLeadResponse,
  UpdateMyProfileRequest,
  VerifyEmailRequest,
} from '../types/agent.types'

/** Сырая форма верификации из сгенерированного клиента (все поля optional). */
type RawCompanyVerification = {
  id?: string
  verificationStatus?: string
  typedCompanyName?: string
  candidates?: Array<{
    companyId?: string
    name?: string
    website?: string
    city?: string
    country?: string
    confidence?: number
    sourceUrl?: string
  }>
  selectedCompanyId?: string
  confidence?: number
}

/** Маппинг V1CompanyVerification → ручной тип фичи. grpc-gateway опускает пустой repeated → [] по умолчанию. */
function mapCompanyVerification(v: RawCompanyVerification): CompanyVerification {
  const candidates: CompanyCandidate[] = (v.candidates ?? []).map((c) => ({
    companyId: c.companyId ?? '',
    name: c.name ?? '',
    website: c.website,
    city: c.city,
    country: c.country,
    confidence: c.confidence ?? 0,
    sourceUrl: c.sourceUrl,
  }))

  return {
    id: v.id ?? '',
    verificationStatus: (v.verificationStatus ?? 'pending_ai') as CompanyVerificationStatus,
    typedCompanyName: v.typedCompanyName,
    candidates,
    selectedCompanyId: v.selectedCompanyId,
    confidence: v.confidence,
  }
}

export const agentsApi = {
  /** POST /api/v1/agents/register (публичный, капча) */
  async register(payload: AgentRegisterRequest): Promise<AgentRegisterResponse> {
    const { data } = await agentsServiceApi.agentsServiceRegister(payload)
    return data as AgentRegisterResponse
  },

  /** POST /api/v1/agents/leads/check-unique (роль AGENT) */
  async checkLeadUnique(payload: CheckLeadUniqueRequest): Promise<CheckLeadUniqueResponse> {
    const { data } = await agentsServiceApi.agentsServiceCheckLeadUnique(payload)
    return data as CheckLeadUniqueResponse
  },

  /** POST /api/v1/agents/leads/submit (роль AGENT) */
  async submitLead(payload: SubmitLeadRequest): Promise<SubmitLeadResponse> {
    const { data } = await agentsServiceApi.agentsServiceSubmitLead(payload)
    return data as SubmitLeadResponse
  },

  /** GET /api/v1/agents/me (роль AGENT) */
  async getMyProfile(): Promise<AgentProfile> {
    const { data } = await agentsServiceApi.agentsServiceGetMyProfile()
    return data as AgentProfile
  },

  /**
   * PUT /api/v1/agents/me (роль AGENT, DEV-1015) — визитка: WhatsApp/Telegram/фото.
   * Через сырой apiClient: поля whatsapp/photo_file_id свежие и пока отсутствуют в
   * сгенерированном клиенте (аналогично getBotLinkCode). На бэкенде "" = не менять.
   */
  async updateMyProfile(payload: UpdateMyProfileRequest): Promise<AgentProfile> {
    const { data } = await apiClient.put<AgentProfile>('/api/v1/agents/me', payload)
    return data
  },

  /**
   * GET /api/v1/agents/me/company-verification (роль AGENT, DEV-794) — открытая верификация
   * компании агента с кандидатами. null — открытой верификации нет.
   */
  async getMyCompanyVerification(): Promise<CompanyVerification | null> {
    const { data } = await agentsServiceApi.agentsServiceGetMyCompanyVerification()
    return data.verification ? mapCompanyVerification(data.verification) : null
  },

  /**
   * POST /api/v1/agents/me/company-verification/select (роль AGENT, DEV-794) — агент выбирает
   * свою компанию из кандидатов (companyId). Возвращает обновлённую верификацию.
   */
  async confirmCompanyAssignment(selectedCompanyId: string): Promise<CompanyVerification | null> {
    const { data } = await agentsServiceApi.agentsServiceConfirmCompanyAssignmentByAgent({
      selectedCompanyId,
    })
    return data.verification ? mapCompanyVerification(data.verification) : null
  },

  /** GET /api/v1/agents/me/deals (роль AGENT) — сделки агента с маскированным именем клиента */
  async getMyDeals(): Promise<GetMyDealsResponse> {
    const { data } = await agentsServiceApi.agentsServiceListMyDeals()
    return data as GetMyDealsResponse
  },

  /** GET /api/v1/agents/me/dashboard (роль AGENT) — KPI кабинета (DEV-489/490). */
  async getAgentDashboard(): Promise<AgentDashboard> {
    const { data } = await agentsServiceApi.agentsServiceGetAgentDashboard()
    return {
      clientsCount: Number(data.clientsCount ?? 0),
      activeDealsCount: Number(data.activeDealsCount ?? 0),
      depositsCount: Number(data.depositsCount ?? 0),
      expectedRewardUsdMinor: Number(data.expectedRewardUsd?.amountMinor ?? 0),
      lastActivityAt: data.lastActivityAt,
      nextTask: data.nextTask
        ? {
            taskId: data.nextTask.taskId ?? '',
            title: data.nextTask.title ?? '',
            dueDate: data.nextTask.dueDate,
          }
        : undefined,
    }
  },

  /** GET /api/v1/agents/me/clients (роль AGENT) — переданные клиенты, public-safe (DEV-489/491). */
  async getMyClients(): Promise<GetMyClientsResponse> {
    const { data } = await agentsServiceApi.agentsServiceListMyClients()
    return {
      clients: (data.clients ?? []).map((c) => ({
        clientId: c.clientId ?? '',
        nameMasked: c.nameMasked ?? '',
        leadSource: c.leadSource ?? '',
        hasDeal: c.hasDeal ?? false,
        clientStatusPublic: c.clientStatusPublic ?? '',
        nextStepPublic: c.nextStepPublic ?? '',
      })),
    }
  },

  /**
   * GET /api/v1/agents/me/education/videos (роль AGENT) — обучающие видео вместе с
   * прогрессом текущего агента (DEV-1768). Один запрос на отрисовку каталога: прогресс
   * приезжает внутри карточки, отдельного похода за ним нет.
   */
  async getEducationVideos(): Promise<GetEducationVideosResponse> {
    const { data } = await agentsServiceApi.agentsServiceListEducationVideos()
    return {
      videos: (data.videos ?? []).map(
        (v): AgentEducationVideo => ({
          id: v.id ?? '',
          slug: v.slug ?? '',
          videoUrl: v.videoUrl ?? '',
          coverUrl: v.coverUrl ?? '',
          durationSec: v.durationSec ?? 0,
          title: v.title ?? '',
          description: v.description ?? '',
          progress: {
            watchedSeconds: v.progress?.watchedSeconds ?? 0,
            lastPositionSec: v.progress?.lastPositionSec ?? 0,
            completed: v.progress?.completed ?? false,
            completedAt: v.progress?.completedAt,
            lastWatchedAt: v.progress?.lastWatchedAt,
          },
        }),
      ),
    }
  },

  /**
   * POST /api/v1/agents/me/education/videos/{videoId}/track (роль AGENT) — батч событий
   * плеера (DEV-1770). Возвращает пересчитанный прогресс: сервер считает по покрытым
   * отрезкам таймлайна, поэтому перемотка в конец ничего не засчитывает.
   */
  async trackEducationVideo(videoId: string, events: PlaybackEvent[]): Promise<AgentVideoProgress> {
    const { data } = await agentsServiceApi.agentsServiceTrackEducationVideo(videoId, { events })
    const p = data.progress
    return {
      watchedSeconds: p?.watchedSeconds ?? 0,
      lastPositionSec: p?.lastPositionSec ?? 0,
      completed: p?.completed ?? false,
      completedAt: p?.completedAt,
      lastWatchedAt: p?.lastWatchedAt,
    }
  },

  /**
   * Тот же трек, но переживающий уход со страницы: обычный XHR при закрытии вкладки
   * браузер обрывает, и последние секунды просмотра теряются.
   *
   * fetch с keepalive, а не sendBeacon: beacon не умеет заголовки, а ручка требует
   * Authorization — токен в query бэкенд не принимает. Тело у keepalive ограничено
   * 64 КБ, батчу событий этого хватает с запасом.
   */
  trackEducationVideoKeepalive(
    videoId: string,
    events: PlaybackEvent[],
    accessToken: string,
  ): void {
    const base = apiClient.defaults.baseURL ?? ''
    const url = `${base}/api/v1/agents/me/education/videos/${videoId}/track`

    void fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ events }),
    }).catch(() => undefined)
  },

  /** GET /api/v1/agents/me/webinars (роль AGENT) — вебинары + статус участия (DEV-489/495). */
  async getWebinars(): Promise<GetWebinarsResponse> {
    const { data } = await agentsServiceApi.agentsServiceListWebinars()
    return {
      webinars: (data.webinars ?? []).map((w) => ({
        id: w.id ?? '',
        title: w.title ?? '',
        description: w.description ?? '',
        coverUrl: w.coverUrl ?? '',
        joinUrl: w.joinUrl ?? '',
        recordingUrl: w.recordingUrl ?? '',
        scheduledAt: w.scheduledAt,
        durationMinutes: w.durationMinutes ?? 0,
        status: w.status ?? '',
        registered: w.registered ?? false,
        attended: w.attended ?? false,
      })),
    }
  },

  /** POST /api/v1/agents/me/webinars/{webinarId}/register (роль AGENT). */
  async registerForWebinar(webinarId: string): Promise<void> {
    await agentsServiceApi.agentsServiceRegisterForWebinar(webinarId, {})
  },

  /**
   * POST /api/v1/agents/deals/{dealId}/commission-question (роль AGENT) — вопрос по
   * вознаграждению. Тело пустое (deal_id из пути). Если даты нет — бэкенд заводит обращение.
   */
  async askCommission(dealId: string): Promise<AskCommissionResponse> {
    const { data } = await agentsServiceApi.agentsServiceAskCommission(dealId, {})
    return data as AskCommissionResponse
  },

  /** POST /api/v1/agents/deals/{dealId}/meeting (роль AGENT) — заявка на встречу по сделке */
  async requestMeeting(
    dealId: string,
    payload: RequestMeetingPayload,
  ): Promise<RequestMeetingResponse> {
    const { data } = await agentsServiceApi.agentsServiceRequestMeeting(dealId, payload)
    return data as RequestMeetingResponse
  },

  /** POST /api/v1/agents/inquiries (роль AGENT) — свободное обращение (другой вопрос / стратегии) */
  async submitInquiry(inquiryType: InquiryType, text: string): Promise<SubmitInquiryResponse> {
    const { data } = await agentsServiceApi.agentsServiceSubmitInquiry({ inquiryType, text })
    return data as SubmitInquiryResponse
  },

  /**
   * POST /api/v1/agents/me/bot-link-code (роль AGENT) — код привязки Telegram-бота + deep-link.
   * Через сырой apiClient (а не agentsServiceApi): метод свежий и пока отсутствует в сгенерированном
   * клиенте — аналогично staff-флоу (useAgencyBot, DEV-344). Тело пустое.
   */
  async getBotLinkCode(): Promise<AgentBotLinkCode> {
    const { data } = await apiClient.post<AgentBotLinkCode>('/api/v1/agents/me/bot-link-code', {})
    return data
  },

  /**
   * GET /api/v1/agents/me/tickets (роль AGENT) — обращения агента (DEV-433/438).
   * grpc-gateway опускает пустые repeated-поля → нормализуем tickets к массиву.
   */
  async listMyTickets(): Promise<GetMyTicketsResponse> {
    const { data } = await agentsServiceApi.agentsServiceListMyTickets()
    return { tickets: (data.tickets ?? []) as AgentTicket[] }
  },

  /**
   * GET /api/v1/agents/me/tickets/{id} (роль AGENT) — обращение + переписка.
   * Нормализуем messages к массиву (пустой repeated опускается шлюзом), ticket — к
   * объекту: иначе доступ thread.messages.length / thread.ticket.* в треде упал бы.
   */
  async getMyTicket(id: string): Promise<AgentTicketThread> {
    const { data } = await agentsServiceApi.agentsServiceGetMyTicket(id)
    return {
      ticket: (data.ticket ?? {}) as AgentTicket,
      messages: (data.messages ?? []) as AgentTicketMessage[],
    }
  },

  /** POST /api/v1/agents/me/tickets/{id}/messages (роль AGENT) — дописать в переписку */
  async postTicketMessage(id: string, body: string): Promise<void> {
    await agentsServiceApi.agentsServicePostAgentTicketMessage(id, { body })
  },

  /**
   * GET /api/v1/agents/projects/{rc_id}/materials (роль AGENT, DEV-1021) — материалы ЖК
   * для клиента (приватный бакет, presigned downloadUrl). Через сырой apiClient: ручка
   * свежая, в сгенерированном клиенте её нет. Пустой repeated опускается шлюзом → [].
   */
  async getProjectMaterials(rcId: string): Promise<AgentProjectMaterial[]> {
    const { data } = await apiClient.get<{ materials?: Array<Partial<AgentProjectMaterial>> }>(
      `/api/v1/agents/projects/${rcId}/materials`,
    )
    return (data.materials ?? []).map((m) => ({
      category: (m.category ?? 'documents') as MaterialCategory,
      title: m.title ?? '',
      locale: m.locale || undefined,
      downloadUrl: m.downloadUrl ?? '',
      contentType: m.contentType ?? '',
      sizeBytes: Number(m.sizeBytes ?? 0),
      sortOrder: Number(m.sortOrder ?? 0),
    }))
  },

  /**
   * GET /api/v1/agents/deals/{deal_id}/free-slots?from&to (роль AGENT, DEV-1021) —
   * свободные окна куратора. Диапазон — RFC3339. Пустой repeated → [].
   */
  async getDealFreeSlots(dealId: string, from: string, to: string): Promise<AgentFreeSlot[]> {
    const { data } = await apiClient.get<{ slots?: AgentFreeSlot[] }>(
      `/api/v1/agents/deals/${dealId}/free-slots`,
      { params: { from, to } },
    )
    return (data.slots ?? []).map((s) => ({ start: s.start ?? '', end: s.end ?? '' }))
  },

  /** GET /api/v1/agents/deals/{deal_id}/comments (роль AGENT, DEV-1021) — свои комментарии по сделке. */
  async listDealComments(dealId: string): Promise<AgentDealComment[]> {
    const { data } = await apiClient.get<{ comments?: AgentDealComment[] }>(
      `/api/v1/agents/deals/${dealId}/comments`,
    )
    return data.comments ?? []
  },

  /** POST /api/v1/agents/deals/{deal_id}/comments {text} (роль AGENT, DEV-1021). */
  async addDealComment(dealId: string, text: string): Promise<AgentDealComment> {
    const { data } = await apiClient.post<AgentDealComment>(
      `/api/v1/agents/deals/${dealId}/comments`,
      { text },
    )
    return data
  },

  /** GET /api/v1/agents/clients/{client_id}/comments (роль AGENT, DEV-1021). */
  async listClientComments(clientId: string): Promise<AgentClientComment[]> {
    const { data } = await apiClient.get<{ comments?: AgentClientComment[] }>(
      `/api/v1/agents/clients/${clientId}/comments`,
    )
    return data.comments ?? []
  },

  /** POST /api/v1/agents/clients/{client_id}/comments {content} (роль AGENT, DEV-1021). */
  async addClientComment(clientId: string, content: string): Promise<AgentClientComment> {
    const { data } = await apiClient.post<AgentClientComment>(
      `/api/v1/agents/clients/${clientId}/comments`,
      { content },
    )
    return data
  },

  /**
   * POST /api/v1/agents/deals/{deal_id}/tasks (роль AGENT, DEV-1021) — задача по свободному
   * слоту. Занятый слот → HTTP 409 (обрабатывается во view: «слот занят» + refetch).
   */
  async createDealTask(dealId: string, payload: CreateDealTaskRequest): Promise<AgentDealTask> {
    const { data } = await apiClient.post<AgentDealTask>(
      `/api/v1/agents/deals/${dealId}/tasks`,
      payload,
    )
    return data
  },
}

/** Подсказка компании для автокомплита поля «Агентство» (DEV-1052). */
export interface CompanySuggestion {
  id: string
  name: string
  city: string
}

/** Публичные ручки auth — переиспользуются агентской панелью (reset/verify). */
export const agentsAuthApi = {
  /**
   * GET /api/v1/agents/company-search — публичные подсказки партнёрских компаний по имени
   * (от 2 символов) для поля «Агентство» на форме регистрации агента (DEV-1052).
   */
  async searchCompanies(query: string, limit = 10): Promise<CompanySuggestion[]> {
    const { data } = await apiClient.get<{ companies?: CompanySuggestion[] }>(
      '/api/v1/agents/company-search',
      { params: { query, limit } },
    )

    return data.companies ?? []
  },

  /** POST /api/v1/auth/password/reset/request — нейтральный ответ (анти-энумерация) */
  async requestPasswordReset(payload: RequestPasswordResetRequest): Promise<void> {
    await apiClient.post('/api/v1/auth/password/reset/request', payload)
  },

  /**
   * POST /api/v1/auth/password/reset/confirm.
   * Шлюз auth принимает ТОЛЬКО proto-имена полей (snake_case), camelCase игнорируется —
   * поэтому отправляем new_password явно (иначе сервер видит пустой пароль).
   */
  async confirmPasswordReset(payload: ConfirmPasswordResetRequest): Promise<void> {
    await apiClient.post('/api/v1/auth/password/reset/confirm', {
      token: payload.token,
      new_password: payload.newPassword,
    })
  },

  /** POST /api/v1/auth/email/verify — PENDING → ACTIVE */
  async verifyEmail(payload: VerifyEmailRequest): Promise<void> {
    await apiClient.post('/api/v1/auth/email/verify', payload)
  },

  /**
   * GET /api/v1/auth/invitations/token/{token} — приглашение по токену из
   * письма (DEV-1308). Публичный метод: экран открывается без сессии.
   *
   * Сюда ведут приглашения ролей, открывающих только кабинет агента; развилку
   * между crm.{host} и agents.{host} считает auth по apps ролей приглашения.
   */
  async getInvitationByToken(token: string): Promise<{ email: string }> {
    const { data } = await apiClient.get<{ email?: string }>(
      `/api/v1/auth/invitations/token/${encodeURIComponent(token)}`,
    )

    return { email: data.email ?? '' }
  },

  /**
   * POST /api/v1/auth/invitations/accept — заводит учётку с ролями из
   * приглашения и сразу отдаёт пару токенов.
   *
   * Имена полей — proto: шлюз auth принимает и отдаёт только snake_case, как и
   * в сбросе пароля выше. Ответ при этом даёт `access_token`, а не
   * `tokens.accessToken` как логин: там поля так и названы в схеме.
   */
  async acceptInvitation(payload: {
    token: string
    firstName: string
    lastName: string
    password: string
  }): Promise<{ accessToken: string; refreshToken: string }> {
    const { data } = await apiClient.post<{ access_token?: string; refresh_token?: string }>(
      '/api/v1/auth/invitations/accept',
      {
        token: payload.token,
        first_name: payload.firstName,
        last_name: payload.lastName,
        password: payload.password,
      },
    )

    return {
      accessToken: data.access_token ?? '',
      refreshToken: data.refresh_token ?? '',
    }
  },
}
